/**
 * Base service class with offline support
 */

import { InternalSDKOperations } from "../types";
import { NetworkError } from "../errors";
import { NetworkDetectionManager } from "../utils/network-detection-manager";

/**
 * Base service class that provides offline-first operation handling
 * with centralized network detection and consistent error handling
 */
export abstract class BaseService {
  protected internalOps: InternalSDKOperations;
  protected abstract serviceName: string;
  private networkManager?: NetworkDetectionManager;

  constructor(
    internalOps: InternalSDKOperations,
    networkManager?: NetworkDetectionManager
  ) {
    this.internalOps = internalOps;
    this.networkManager = networkManager;
  }

  /**
   * Executes an operation with offline support
   *
   * This method provides offline-first operation handling:
   * 1. If offline mode is disabled, executes operation normally
   * 2. If offline, queues the operation for later sync
   * 3. If online but operation fails with network error, queues it
   * 4. Otherwise, executes the operation immediately
   *
   * @template T - The expected return type of the operation
   * @param methodName - The name of the method being executed (for logging and queuing)
   * @param parameters - The parameters to pass to the queued operation
   * @param onlineOperation - The function to execute when online
   * @returns Promise that resolves to the operation result or empty object for queued operations
   *
   * @example
   * ```typescript
   * const functionName = 'addToFavorites';
   * const parameters = { wordId: '123', language: 'en' };
   *
   * await this.executeWithOfflineSupport(
   *   functionName,
   *   parameters,
   *   () => this.internalOps.makeRequest('favorites', functionName, parameters)
   * );
   * ```
   */
  protected async executeWithOfflineSupport<T>(
    methodName: string,
    parameters: Record<string, any>,
    onlineOperation: () => Promise<T>
  ): Promise<T> {
    return this.executeOperation(methodName, parameters, onlineOperation);
  }

  /**
   * Internal method to execute the operation with offline support
   *
   * This is the core logic that determines whether to execute immediately,
   * queue for later, or handle network errors gracefully.
   *
   * @template T - The expected return type of the operation
   * @param methodName - The name of the method being executed
   * @param parameters - The parameters for the operation
   * @param onlineOperation - The function to execute when online
   * @returns Promise that resolves to the operation result
   */
  private async executeOperation<T>(
    methodName: string,
    parameters: Record<string, any>,
    onlineOperation: () => Promise<T>
  ): Promise<T> {
    // If offline mode is enabled, check connectivity first
    if (this.internalOps.isOfflineModeEnabled?.()) {
      // If we're offline, queue immediately without trying network
      if (!this.isOnline()) {
        await this.internalOps.queueOperation?.(
          this.serviceName,
          methodName,
          parameters
        );
        console.log(
          `Operation queued (offline): ${this.serviceName}.${methodName}`
        );
        return {} as T; // Return empty response for queued operations
      }
    }

    // Try online operation first
    try {
      return await onlineOperation();
    } catch (error) {
      // If offline mode is enabled and this is a network error, queue it
      if (
        this.internalOps.isOfflineModeEnabled?.() &&
        this.isNetworkError(error)
      ) {
        await this.internalOps.queueOperation?.(
          this.serviceName,
          methodName,
          parameters
        );
        console.log(
          `Operation queued (network error): ${this.serviceName}.${methodName}`
        );
        return {} as T; // Return empty response for queued operations
      }

      // Re-throw non-network errors or when offline mode is disabled
      throw error;
    }
  }

  /**
   * Check if currently online using the network manager
   */
  private isOnline(): boolean {
    if (this.networkManager) {
      return this.networkManager.isOnline;
    }

    // Fallback to always online if network manager not available
    return true;
  }

  /**
   * Check if an error is a network-related error
   * Provides comprehensive network error detection
   */
  private isNetworkError(error: any): boolean {
    // Check for known network error types
    if (error instanceof NetworkError || error instanceof TypeError) {
      return true;
    }

    // Check for specific network error codes
    const errorCodes = ["ENOTFOUND", "ECONNREFUSED", "ETIMEDOUT", "ECONNRESET"];
    const errorCode = (error as Error & { cause?: { code: string } })?.cause
      ?.code;
    if (errorCode && errorCodes.includes(errorCode)) {
      return true;
    }

    // Check error message for network-related keywords
    const message = error?.message?.toLowerCase() || "";
    const networkKeywords = [
      "network",
      "fetch",
      "connection",
      "timeout",
      "offline",
    ];
    
    return networkKeywords.some(keyword =>
      new RegExp(`\\b${keyword}\\b`).test(message)
    );
  }

}
